<template>
	<div class="flex gap-x-8 items-center lg:text-base text-sm">
		<span class="mr-2">{{ $t("Page") }}</span>

		<div class="flex items-center gap-x-4">
			<NuxtLink
				v-show="!isFirstPage"
				:to="prevPageLink"
				class="lg:w-11 lg:h-11 w-[28px] h-[28px] flex items-center justify-center rounded transition-colors bg-gray-200 hover:bg-gray-300"
				:aria-label="$t('Previous page')"
				@click="emitPageChange(currentPage - 1)"
			>
				<ChevronLeftIcon class="h-6 w-6" />
			</NuxtLink>

			<template v-for="(page, index) in displayedPages" :key="index">
				<NuxtLink
					v-if="page !== '...'"
					:to="getPageLink(page)"
					:class="[
						'lg:w-11 lg:h-11 w-[28px] h-[28px] flex items-center justify-center rounded transition-all',
						currentPage === page
							? 'bg-blue text-white'
							: 'bg-gray-200 hover:bg-gray-300'
					]"
					:data-active-page="currentPage === page"
					@click="emitPageChange(page)"
				>
					{{ page }}
				</NuxtLink>
				<span
					v-else
					class="lg:w-11 lg:h-11 w-[28px] h-[28px] flex items-center justify-center rounded bg-gray-200"
				>
					...
				</span>
			</template>

			<NuxtLink
				v-show="!isLastPage"
				:to="nextPageLink"
				class="lg:w-11 lg:h-11 w-[28px] h-[28px] flex items-center justify-center rounded transition-all bg-gray-200 hover:bg-gray-300"
				:aria-label="$t('Next page')"
				@click="emitPageChange(currentPage + 1)"
			>
				<ChevronRightIcon class="h-6 w-6" />
			</NuxtLink>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";

interface Props {
	totalItems: number;
	pageSize: number;
	currentPage: number;
}

const { currentPage, totalItems, pageSize } = defineProps<Props>();
const emit = defineEmits(["getCurrentPageProducts"]);
const route = useRoute();

const totalPages = computed(() => Math.ceil(totalItems / pageSize));
const isFirstPage = computed(() => currentPage === 1);
const isLastPage = computed(() => currentPage === totalPages.value);

const displayedPages = computed(() => {
	const pages: (number | string)[] = [];
	const maxPagesToShow = totalPages.value > 3 ? 3 : totalPages.value;

	if (currentPage > 2 && totalPages.value > 3) {
		pages.push(1);
	}

	if (
		(currentPage === 4 && currentPage !== totalPages.value) ||
		(totalPages.value === 5 && currentPage === totalPages.value)
	) {
		pages.push(2);
	}

	if (currentPage > 4 && totalPages.value > 5) {
		pages.push("...");
	}

	let startPage =
		currentPage === 1
			? 1
			: currentPage === totalPages.value
				? currentPage - 2
				: currentPage - 1;
	if (totalPages.value < 3 && currentPage === totalPages.value) {
		startPage = currentPage - 1;
	}

	for (let i = 0; i < maxPagesToShow; i++) {
		const page = startPage + i;
		if (page <= totalPages.value) {
			pages.push(page);
		}
	}

	if (totalPages.value > 5 && currentPage < totalPages.value - 3) {
		pages.push("...");
	}

	if (
		(totalPages.value > 4 && currentPage === totalPages.value - 3) ||
		(totalPages.value === 5 && currentPage === 1)
	) {
		pages.push(totalPages.value - 1);
	}

	if (totalPages.value >= 4 && currentPage < totalPages.value - 1) {
		pages.push(totalPages.value);
	}

	return pages;
});

const getPageLink = (page: number | string) => {
	if (page === "...") return "#";

	const query = { ...route.query };

	if (page === 1) {
		delete query.pageNo;
	} else {
		query.pageNo = page.toString();
	}

	return { path: route.path, query };
};

const prevPageLink = computed(() => {
	if (isFirstPage.value) return "#";
	return getPageLink(currentPage - 1);
});

const nextPageLink = computed(() => {
	if (isLastPage.value) return "#";
	return getPageLink(currentPage + 1);
});

const emitPageChange = (page: number | string) => {
	if (page >= 1 && page <= totalPages.value) {
		emit("getCurrentPageProducts", Number(page.toString()));
		scrollTo({ top: 0, behavior: "smooth" });
	}
};
</script>
